<template>
  <div class="form">
    <AHeading class="form__header" :level="2">ログイン</AHeading>
    <form class="form__container" @submit.prevent="signin()">
      <div class="form__parts">
        <ALabel for="email">メールアドレス</ALabel>
        <AInput
          class="form__input"
          type="text"
          name="email"
          :value="email"
          placeholder="onecareer@onecareer.jp"
          :validate-rule="emailValidateRule"
          validate-mode="blur"
          @input="handleInput($event, 'email')"
          @validateBlur="handleValidateBlur($event, 'email')"
        />
      </div>
      <div class="form__parts">
        <ALabel class="form__label">パスワード</ALabel>
        <ABody class="form__body" size="small">8文字以上の半角英数字記号</ABody>
        <AInput
          class="form__input"
          type="password"
          name="password"
          :value="password"
          placeholder="************"
          :validate-rule="passwordValidateRule"
          validate-mode="blur"
          @input="handleInput($event, 'password')"
          @validateBlur="handleValidateBlur($event, 'password')"
        />
      </div>
      <div class="form__checkbox">
        <ACheckbox :value="checked" :data-value="true" @change="handleChange"
          ><span class="form__checkbox-body"
            >ログイン情報を保持する</span
          ></ACheckbox
        >
      </div>
      <div class="form__button-wrapper">
        <AButton
          round
          fill
          bold
          size="medium"
          :disabled="buttonDisabled"
          :processing="processing"
          :prevent-default="buttonDisabled"
          class="form__button"
          >ログイン</AButton
        >
      </div>
    </form>
    <div v-for="(link, index) in links" :key="index" class="form__link">
      <ALink :to="link.to">{{ link.text }}</ALink>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import * as api from '@/api/index';
import AHeading from '@/components/atoms/ATypography/AHeading/index.vue';
import ALabel from '@/components/atoms/ATypography/ALabel/index.vue';
import ABody from '@/components/atoms/ATypography/ABody/index.vue';
import ALink from '@/components/atoms/ATypography/ALink/index.vue';
import AButton from '@/components/atoms/AButton/index.vue';
import AInput from '@/components/atoms/AForm/AInput/index.vue';
import ACheckbox from '@/components/atoms/AForm/ACheckbox/index.vue';
import {
  emailValidateRule,
  passwordValidateRule
} from '@/helpers/validateRules';
import { ErrorEmailMessage } from '@/helpers/errorMessages';

const signedKey = 'signed_in_now' as const;

export default defineComponent({
  name: 'SignIn',
  components: {
    AHeading,
    ALabel,
    ABody,
    ALink,
    AButton,
    AInput,
    ACheckbox
  },
  props: {
    errorMessage: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      email: '',
      password: '',
      checked: true,
      processing: false,
      errorsEmail: false,
      errorsPassword: false,
      emailValidateRule: emailValidateRule,
      passwordValidateRule: passwordValidateRule,
      links: [
        { to: '/users/password/new', text: 'パスワードを忘れてしまった場合 >' },
        { to: '/users/sign_up', text: '新規登録はこちら >' }
      ]
    };
  },
  computed: {
    buttonDisabled() {
      return (
        !this.email || !this.password || this.errorsEmail || this.errorsPassword
      );
    }
  },
  created() {
    if (this.errorMessage) {
      alert(`${this.errorMessage}`);
    }
  },
  async mounted() {
    // NOTE: 認証済でログイン保持の場合はプラストップに遷移する
    const userSessionsVerify = await api.postUserSessionsVerify();
    const response = await api.get('/api/v1/auth/keep_login');
    const ocpOrigin = process.env['VUE_APP_ONECAREER_PLUS_ORIGIN'];
    if (userSessionsVerify.status === 200 && response.data.keep_login) {
      const tokenResponse = await api.get('/api/v1/auth/refresh');
      this.$cookies.set(
        'token',
        tokenResponse.data.token,
        1800,
        '/',
        process.env['VUE_APP_ONECAREERJP_DOMAIN'],
        { secure: true }
      );
      this.$cookies.set(
        signedKey,
        true,
        60,
        '/',
        process.env['VUE_APP_ONECAREERJP_DOMAIN'],
        { secure: true }
      );
      const path = this.$route.query.origin?.startsWith(`${ocpOrigin}/`)
        ? this.$route.query.origin
        : ocpOrigin;
      window.location.href = path;
    }
  },
  methods: {
    handleInput(value, target) {
      if (target === 'email') {
        this.email = value;
      } else if (target === 'password') {
        this.password = value;
      }
    },
    handleValidateBlur(error, target) {
      if (target === 'email') {
        this.errorsEmail = error;
      } else if (target === 'password') {
        this.errorsPassword = error;
      }
    },
    handleChange(checkedData) {
      this.checked = !!checkedData.value.length;
    },
    async signin() {
      // ボタン非活性の場合は無効
      if (this.buttonDisabled) return false;

      try {
        this.processing = true;
        const response = await api
          .post('/api/v1/user_sessions', {
            email: this.email,
            password: this.password,
            keep_login: this.checked
          })
          .finally(() => {
            this.processing = false;
          });
        const ocpOrigin = process.env['VUE_APP_ONECAREER_PLUS_ORIGIN'];
        if (response.data.access_token === undefined) {
          const register_token = response.data.register_token;
          window.location.href = `${ocpOrigin}/users/invitation/accept?register_token=${register_token}`;
          return;
        }

        this.$cookies.set(
          'token',
          response.data.access_token,
          1800,
          '/',
          process.env['VUE_APP_ONECAREERJP_DOMAIN'],
          { secure: true }
        );
        this.$cookies.set(
          signedKey,
          true,
          60,
          '/',
          process.env['VUE_APP_ONECAREERJP_DOMAIN'],
          { secure: true }
        );

        if (
          this.$route.query &&
          this.$route.query.origin?.startsWith(`${ocpOrigin}/`)
        ) {
          window.location = this.$route.query.origin;
        } else {
          this.$router.push('/');
        }
      } catch (error) {
        alert(ErrorEmailMessage);
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.form {
  @include formBase;

  &__checkbox {
    max-width: fit-content;
    margin-top: 16px;
    font-size: 12px;
  }

  &__checkbox-body {
    margin-left: 8px;
  }

  &__link {
    margin-top: 12px;
    text-align: right;
  }

  &__header {
    margin-bottom: 32px;
  }
}
</style>
